import { ActionIcon, Badge, Box, Card, Flex, Grid, Group, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBrandFacebook,
  IconClock,
  IconHistory,
  IconStar,
  IconStarFilled,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import prettifiedLabel from '../../constants/labelPrettifier';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import SegmentStats from './segmentStats';

const SegmentsCards = ({ segments, stats }) => {
  const currentDomain = useSelector((state) => state.domain.domain);
  const domainCurrency = currentDomain.currency;
  const fetch = useFetch();

  // Stars are toggled optimistically, keyed by segment id: the listing is fetched
  // by the parent ListingPage, which offers no way to ask it for a refetch.
  const [starredById, setStarredById] = useState({});
  // Tracks which segment ids have a star PUT in flight, so a second click
  // can't fire a second write whose response could race the first and
  // overwrite its rollback.
  const [pendingIds, setPendingIds] = useState(new Set());

  const location = useLocation();

  const toggleStarred = (segment, starred) => {
    setStarredById((current) => ({ ...current, [segment.id]: starred }));
    setPendingIds((current) => new Set(current).add(segment.id));
    // Send only the field that changed, like toggleCampaign does for
    // campaigns (CampaignsPageCommon.jsx) — segment comes from the listing's
    // GET /segments collection response, which may be a lighter projection
    // than the single-segment GET /segments/:id; echoing it back on a
    // full-replace PUT would risk dropping whatever it omits.
    fetch(`/api/admin/accounts/${currentDomain.accountId}/domains/${currentDomain.id}/segments/${segment.id}`, {
      method: 'PUT',
      body: JSON.stringify({ starred }),
    })
      .catch((error) => {
        // The backend caps starred segments per domain and rejects the extra one
        // with a message naming the cap. Roll the optimistic star back, or it would
        // stay lit on a segment Audience Performance does not actually track.
        setStarredById((current) => ({ ...current, [segment.id]: !starred }));
        notifications.show({
          title: starred ? 'Could not star segment' : 'Could not unstar segment',
          message: getErrorMessage(error),
          color: 'red',
        });
      })
      .finally(() => {
        setPendingIds((current) => {
          const next = new Set(current);
          next.delete(segment.id);
          return next;
        });
      });
  };

  const segmentElements = [];
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const starred = starredById[segment.id] ?? Boolean(segment.starred);
    const starPending = pendingIds.has(segment.id);

    segmentElements.push(
      <Card key={segment.id} mb="lg" data-segment-name={segment.name} withBorder>
        <Card.Section inheritPadding py="sm" bg="white" style={{ textAlign: 'left' }}>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 9 }}>
              <Group gap="xs" wrap="nowrap">
                <Tooltip
                  label={starred ? 'Stop tracking in Audience Performance' : 'Track in Audience Performance'}
                  position="top"
                  withArrow
                >
                  <ActionIcon
                    variant="subtle"
                    color={starred ? 'yellow' : 'gray'}
                    aria-label={starred ? `Unstar ${segment.name}` : `Star ${segment.name}`}
                    onClick={() => toggleStarred(segment, !starred)}
                    loading={starPending}
                    disabled={starPending}
                  >
                    {starred ? <IconStarFilled size={16} /> : <IconStar size={16} />}
                  </ActionIcon>
                </Tooltip>
                <Text c="dimmed">
                  <a style={{ textAlign: 'left' }} href={`#${location.pathname}/${segment.id}`}>
                    <strong>{segment.name}</strong>
                  </a>
                </Text>
              </Group>
              <Flex gap="xs" wrap="wrap">
                <Badge variant="default">
                  {segment.scope === 'facebook' ? <IconBrandFacebook size={14} /> : <IconUsers size={14} />}
                  &nbsp;
                  {prettifiedLabel(segment.scope)}
                </Badge>
                <Tooltip
                  label={
                    <>
                      <div>Created: {new Date(segment.createdAt).toLocaleString()}</div>
                      <div>Updated: {new Date(segment.updatedAt).toLocaleString()}</div>
                    </>
                  }
                  position="top"
                  withArrow
                >
                  <Badge variant="default" style={{ cursor: 'pointer' }}>
                    <IconHistory size={14} style={{ verticalAlign: 'middle' }} />
                  </Badge>
                </Tooltip>
                {segment.lastCalculatedCount != null && segment.lastCalculatedAt && (
                  <>
                    <Badge variant="default" ml="xs">
                      <IconUsersGroup size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {segment.lastCalculatedCount.toLocaleString(navigator.language)}
                    </Badge>
                    <Tooltip
                      label={`Calculated: ${new Date(segment.lastCalculatedAt).toLocaleString()}`}
                      position="top"
                      withArrow
                    >
                      <Badge variant="default" ml="xs" style={{ cursor: 'pointer' }}>
                        <IconClock size={14} style={{ verticalAlign: 'middle' }} />
                      </Badge>
                    </Tooltip>
                  </>
                )}
              </Flex>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 3 }} style={{ textAlign: 'right' }}>
              <Box>
                <Text size="sm" c="dimmed">
                  Rules:
                </Text>
                <Box>
                  {segment.rules ? (
                    <Text size="sm" c="dimmed">
                      {segment.rules
                        .map((rule) => {
                          return rule.inverseSelection ? `Not ${rule.name}` : rule.name;
                        })
                        .join(', ')}
                    </Text>
                  ) : (
                    ''
                  )}
                </Box>
              </Box>
            </Grid.Col>
          </Grid>
        </Card.Section>
        <Card.Section inheritPadding py="sm">
          <Grid>
            <SegmentStats stats={stats} segment={segment} currency={domainCurrency} />
          </Grid>
        </Card.Section>
      </Card>
    );
  }

  return segmentElements;
};

export default SegmentsCards;
